import React, { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Modal, Picture } from "./Home";
import { BACKEND_URL } from "./backendconfig";
import "./Home.css";
import "./CourseDetails.css";

const backend = BACKEND_URL.replace(/\/+$/, "");

function formatCoursePrice(value) {
  if (value === null || value === undefined || value === "") {
    return "Contact us for fees";
  }

  const amount = Number(value);

  if (!Number.isFinite(amount)) {
    return String(value);
  }

  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(amount);
}

function resolveBackendUrl(value) {
  if (!value) return "";

  try {
    const url = new URL(value, `${backend}/`);

    return ["http:", "https:"].includes(url.protocol) ? url.href : "";
  } catch {
    return "";
  }
}
function normalizeTopics(items) {
  if (!Array.isArray(items)) return [];

  return items.map((topic) => ({
    ...topic,
    id: String(topic.id),
    children: normalizeTopics(topic.children),
  }));
}

function findTopic(items, id) {
  if (!Array.isArray(items)) return null;

  for (const topic of items) {
    if (String(topic.id) === id) return topic;

    const found = findTopic(topic.children, id);

    if (found) return found;
  }

  return null;
}

export default function CourseDetails() {
  const { courseId } = useParams();

  const [result, setResult] = useState({
    courseId: null,
    course: null,
    videos: [],
    loading: true,
    error: "",
  });

  const [activeTopicId, setActiveTopicId] = useState("all");
  const [topics, setTopics] = useState([]);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [expandedFolderIds, setExpandedFolderIds] = useState([]);
  useEffect(() => {
    const controller = new AbortController();

    setActiveTopicId("all");
    setTopics([]);
    setExpandedFolderIds([]);
    setSelectedVideo(null);

    window.scrollTo(0, 0);

    setResult({
      courseId,
      course: null,
      videos: [],
      loading: true,
      error: "",
    });

    async function loadCourse() {
      try {
        const response = await fetch(
          `${backend}/course-details.php?courseId=${encodeURIComponent(
            courseId,
          )}`,
          {
            signal: controller.signal,
            cache: "no-store",
          },
        );

        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.message || "Unable to load this course.");
        }

        if (!data.course || !Array.isArray(data.videos)) {
          throw new Error("The backend returned invalid course data.");
        }

        const videos = data.videos.map((video) => ({
          ...video,
          thumbnail: resolveBackendUrl(video.thumbnail),
          topicId: String(video.topicId ?? ""),
        }));

        const courseTopics = normalizeTopics(data.topics);
        if (controller.signal.aborted) return;

        setResult({
          courseId,
          course: data.course,
          videos,
          loading: false,
          error: "",
        });

        setTopics(courseTopics);
      } catch (error) {
        if (controller.signal.aborted || error.name === "AbortError") {
          return;
        }

        setResult({
          courseId,
          course: null,
          videos: [],
          loading: false,
          error: error.message || "Unable to load this course.",
        });
      }
    }

    loadCourse();

    return () => controller.abort();
  }, [courseId]);

  const closePurchasePopup = useCallback(() => {
    setSelectedVideo(null);
  }, []);

  if (result.loading || result.courseId !== courseId) {
    return (
      <main className="ltc-course-details-page">
        <section className="ltc-course-details-section">
          <div className="ltc-course-details-container">
            <p className="ltc-course-details-message" role="status">
              Loading course details…
            </p>
          </div>
        </section>
      </main>
    );
  }

  if (result.error || !result.course) {
    return (
      <main className="ltc-course-details-page">
        <section className="ltc-course-details-section">
          <div className="ltc-course-details-container">
            <div className="ltc-course-details-error">
              <h1 className="ltc-course-details-title">
                Unable to load course
              </h1>

              <p className="ltc-course-details-description" role="alert">
                {result.error || "Course not found."}
              </p>

              <Link to="/#courses" className="ltc-course-details-button">
                Back to Courses
              </Link>
            </div>
          </div>
        </section>
      </main>
    );
  }

  const { course, videos } = result;
  const purchaseUrl = `https://www.embroiderydesigncourse.com/courses/${encodeURIComponent(course.id)}`;

  const visibleVideos =
    activeTopicId === "all"
      ? videos
      : videos.filter((video) => video.topicId === activeTopicId);

  const activeTopicTitle =
    activeTopicId === "all"
      ? "All Topics"
      : findTopic(topics, activeTopicId)?.title || "Course Topic";

  function renderFolderMenu(items) {
    if (!Array.isArray(items)) return null;

    return items.map((topic) => {
      const children = Array.isArray(topic.children) ? topic.children : [];

      const hasSubfolders = children.length > 0;
      const isExpanded = expandedFolderIds.includes(topic.id);
      const isActive = activeTopicId === topic.id;

      return (
        <div className="ltc-sidebar-folder" key={topic.id}>
          <button
            type="button"
            className={`ltc-sidebar-folder-button ${
              isActive ? "ltc-sidebar-folder-active" : ""
            }`}
            {...(hasSubfolders
              ? { "aria-expanded": isExpanded }
              : { "aria-pressed": isActive })}
            onClick={() => {
              if (hasSubfolders) {
                setExpandedFolderIds((current) =>
                  current.includes(topic.id)
                    ? current.filter((id) => id !== topic.id)
                    : [...current, topic.id],
                );
              } else {
                setActiveTopicId(topic.id);
                setSelectedVideo(null);
              }
            }}
          >
            <span className="ltc-sidebar-folder-name">{topic.title}</span>

            {hasSubfolders && (
              <svg
                className={`ltc-sidebar-folder-arrow ${
                  isExpanded ? "ltc-sidebar-folder-arrow-open" : ""
                }`}
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="m6 9 6 6 6-6" />
              </svg>
            )}
          </button>

          {hasSubfolders && isExpanded && (
            <div className="ltc-sidebar-subfolder-list">
              {renderFolderMenu(children)}
            </div>
          )}
        </div>
      );
    });
  }

  return (
    <main className="ltc-course-details-page">
      <section className="ltc-course-details-hero">
        <div className="ltc-course-details-container">
          <Link to="/#courses" className="ltc-course-details-back-link">
            ← Back to Courses
          </Link>

          {/* <span className="ltc-course-details-eyebrow">
            STEP-BY-STEP COURSE PREVIEW
          </span> */}

          <h1 className="ltc-course-details-title">{course.title}</h1>

          <p className="ltc-course-details-description">{course.description}</p>

          {/* <div className="ltc-course-details-purchase-bar">
            <strong className="ltc-course-details-price">
              {formatCoursePrice(course.price)}
            </strong>

            <span className="ltc-course-details-summary">
              {topics.length} Topics Covered
            </span>

            {purchaseUrl && (
              <a
                href={purchaseUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="ltc-course-details-button"
              >
                Buy Now ↗
              </a>
            )}
          </div> */}
        </div>
      </section>

      <section className="ltc-course-details-section">
        <div className="ltc-course-details-container">
          <div className="ltc-course-details-layout">
            <aside
              className="ltc-course-details-sidebar"
              aria-label="Course topics"
            >
              <h2 className="ltc-course-details-sidebar-heading">
                Course Topics
              </h2>

              <div className="ltc-course-details-topic-list">
                {/* <button
                  type="button"
                  className={`ltc-course-details-topic-button${
                    activeTopicId === "all"
                      ? " ltc-course-details-topic-active"
                      : ""
                  }`}
                  aria-pressed={activeTopicId === "all"}
                  onClick={() => setActiveTopicId("all")}
                >
                  {/* <span className="ltc-course-details-topic-number">All</span> 

                  <span className="ltc-course-details-topic-name">
                     All Topics ({videos.length}) 
                    All Topics
                  </span>
                </button> */}

                {renderFolderMenu(topics)}
              </div>
            </aside>

            <div className="ltc-course-details-content">
              <div className="ltc-course-details-content-heading">
                <h2 className="ltc-course-details-content-title">
                  {activeTopicTitle}
                </h2>

                {/* <p className="ltc-course-details-content-description">
                  {visibleVideos.length} video previews. Click a play icon to
                  see purchase details.
                </p> */}
              </div>

              {visibleVideos.length === 0 && (
                <p className="ltc-course-details-message">
                  No preview images have been added to this topic yet.
                </p>
              )}

              <div className="ltc-course-details-video-grid">
                {visibleVideos.map((video) => (
                  <article
                    key={video.id}
                    className="ltc-course-details-video-card"
                  >
                    <button
                      type="button"
                      className="ltc-course-details-thumbnail"
                      aria-label={`Buy ${course.title} to watch ${video.title}`}
                      aria-haspopup="dialog"
                      onClick={() => setSelectedVideo(video)}
                    >
                      <Picture
                        src={video.thumbnail}
                        alt={video.title}
                        loading="lazy"
                      />

                      <span
                        className="ltc-course-details-play-icon"
                        aria-hidden="true"
                      >
                        ▶
                      </span>

                      <span className="ltc-course-details-lock-label">
                        🔒 Purchase to watch
                      </span>
                    </button>

                    <div className="ltc-course-details-video-copy">
                      {/* <span className="ltc-course-details-lesson-label">
                        {video.topicTitle}
                         {String(video.step).padStart(2, "0")} 
                      </span> */}

                      <h3 className="ltc-course-details-video-title">
                        {video.title}
                      </h3>
                    </div>
                  </article>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {selectedVideo && (
        <Modal title="Buy this course to watch" onClose={closePurchasePopup}>
          <div className="ltc-course-details-popup">
            {/* <h2 className="ltc-course-details-popup-title">
              Buy this course to watch this video
            </h2> */}

            <div className="ltc-course-details-popup-image">
              <Picture
                src={selectedVideo.thumbnail}
                alt={selectedVideo.title}
              />
            </div>

            {/* <p className="ltc-course-details-popup-description">
              Purchase <strong>{course.title}</strong> to access its complete
              video topics.
            </p> */}

            {/* <p className="ltc-course-details-popup-price">
              {formatCoursePrice(course.price)}
            </p>

            {purchaseUrl ? (
              <a
                href={purchaseUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="ltc-course-details-button"
              >
                Buy This Course ↗
              </a>
            ) : (
              <Link
                to="/#contact"
                className="ltc-course-details-button"
                onClick={closePurchasePopup}
              >
                Contact Us to Buy
              </Link>
            )} */}
          </div>
        </Modal>
      )}
    </main>
  );
}
